import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import {
  type Characteristics,
  deleteHero,
  type Hero,
  listHeroes,
  renameHero,
  updateCharacteristics,
} from '../lib/heroes';
import { listStories } from '../lib/stories';
import { CostBadge } from '../components/CostBadge';
import { StoryProgress } from '../components/StoryProgress';
import { HERO_COST } from '../data/costs';
import '../components/wizard/wizard.css';
import './Heroes.css';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; heroes: Hero[]; hasStories: boolean }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

type LocationState = { optimisticHero?: Hero } | null;

export function Heroes() {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    Promise.all([listHeroes(), listStories()])
      .then(([heroes, stories]) => {
        if (!cancelled) {
          setState({ kind: 'ready', heroes, hasStories: stories.length > 0 });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setState({ kind: 'unauthenticated' });
          return;
        }
        const message = err instanceof Error ? err.message : t('common.unknownError');
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const incoming = location.state as LocationState;
    if (!incoming?.optimisticHero) return;
    const hero = incoming.optimisticHero;
    setState((prev) =>
      prev.kind === 'ready' && !prev.heroes.some((h) => h.id === hero.id)
        ? { ...prev, heroes: [hero, ...prev.heroes] }
        : prev,
    );
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  function replaceHero(hero: Hero) {
    setState((prev) =>
      prev.kind === 'ready'
        ? {
            ...prev,
            heroes: prev.heroes.map((h) => (h.id === hero.id ? hero : h)),
          }
        : prev,
    );
  }

  function removeHero(id: number) {
    setState((prev) =>
      prev.kind === 'ready'
        ? { ...prev, heroes: prev.heroes.filter((h) => h.id !== id) }
        : prev,
    );
  }

  return (
    <>
      <Body state={state} onUpdate={replaceHero} onDelete={removeHero} />
      <StoryProgress />
    </>
  );
}

function Body({
  state,
  onUpdate,
  onDelete,
}: {
  state: LoadState;
  onUpdate: (h: Hero) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation();
  switch (state.kind) {
    case 'loading':
      return <p className="hero-status">{t('common.loading')}</p>;
    case 'unauthenticated':
      return (
        <p className="hero-status">
          <Trans i18nKey="common.notSignedIn" components={[<a href="/" key="0" />]} />
        </p>
      );
    case 'error':
      return (
        <p className="hero-status hero-error">
          {t('common.error', { message: state.message })}
        </p>
      );
    case 'ready':
      if (state.heroes.length === 0) {
        return (
          <div className="empty-cta">
            <Link
              to="/heroes/new"
              className="round-create round-create--large"
              aria-label={t('heroes.emptyAdd')}
            >
              <span className="round-create__plus" aria-hidden="true">+</span>
              {state.hasStories && <CostBadge cost={HERO_COST} />}
            </Link>
            <p className="empty-cta__label">{t('heroes.emptyAdd')}</p>
            <p className="empty-cta__hint">{t('heroes.emptyHint')}</p>
          </div>
        );
      }
      return (
        <>
          <div className="list-header">
            <h1>{t('heroes.listHeading')}</h1>
            <Link to="/heroes/new" className="create-cta">
              <span className="create-cta__icon" aria-hidden="true">+</span>
              <span className="create-cta__label">{t('heroes.addCta')}</span>
              <CostBadge cost={HERO_COST} />
            </Link>
          </div>
          <div className="hero-list">
            {state.heroes.map((h) => (
              <HeroItem key={h.id} hero={h} onUpdate={onUpdate} onDelete={onDelete} />
            ))}
          </div>
        </>
      );
  }
}

function HeroItem({
  hero,
  onUpdate,
  onDelete,
}: {
  hero: Hero;
  onUpdate: (h: Hero) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!confirm(t('heroes.confirmDelete'))) return;
    setDeleting(true);
    try {
      await deleteHero(hero.id);
      onDelete(hero.id);
    } catch (err) {
      alert(
        t('heroes.deleteFailed', {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
      setDeleting(false);
    }
  }

  const chars = hero.characteristics ?? {};
  const meta = [chars.type, chars.age].filter(Boolean).join(' · ');

  return (
    <div className="hero-item" id={`hero-${hero.id}`}>
      <img src={hero.photo} alt={hero.name} className="hero-photo" />
      <div className="hero-info">
        <h3 className="hero-name">{hero.name}</h3>
        {meta && <p className="characteristics">{meta}</p>}
        {chars.appearance && <p className="appearance">{chars.appearance}</p>}
      </div>
      <div className="hero-actions">
        <button
          type="button"
          className="edit-btn"
          onClick={() => setEditing((v) => !v)}
          title={t('heroes.editTitle')}
          aria-label={t('heroes.editAria', { name: hero.name })}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </button>
        <button
          type="button"
          className="delete-btn"
          onClick={handleDelete}
          disabled={deleting}
          title={t('heroes.deleteTitle')}
          aria-label={t('heroes.deleteAria', { name: hero.name })}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
          </svg>
        </button>
      </div>
      {editing && (
        <EditForm
          hero={hero}
          onSaved={(updated) => {
            onUpdate(updated);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function EditForm({
  hero,
  onSaved,
  onCancel,
}: {
  hero: Hero;
  onSaved: (h: Hero) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const initialKeys = useMemo(() => {
    const keys = Object.keys(hero.characteristics ?? {});
    return keys.length > 0 ? keys : ['type', 'appearance'];
  }, [hero.characteristics]);

  const [name, setName] = useState(hero.name);
  const [chars, setChars] = useState<Characteristics>(() => {
    const initial: Characteristics = {};
    initialKeys.forEach((k) => {
      initial[k] = hero.characteristics?.[k] ?? '';
    });
    return initial;
  });
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const trimmedKey = newKey.trim();
      const nextChars: Characteristics = { ...chars };
      Object.keys(nextChars).forEach((k) => {
        nextChars[k] = nextChars[k].trim();
      });
      if (trimmedKey) nextChars[trimmedKey] = '';

      let updated = await updateCharacteristics(hero.id, nextChars);
      const trimmedName = name.trim();
      if (trimmedName && trimmedName !== hero.name) {
        updated = await renameHero(hero.id, trimmedName);
      }
      onSaved(updated);
    } catch (err) {
      alert(
        t('heroes.saveFailed', {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
      setSaving(false);
    }
  }

  return (
    <div className="edit-form">
      <label>
        {t('common.name')}
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {Object.keys(chars).map((key) => {
        const value = chars[key];
        const long = value.length > 60;
        return (
          <label key={key}>
            {key}
            {long ? (
              <textarea
                value={value}
                onChange={(e) =>
                  setChars((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  setChars((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
            )}
          </label>
        );
      })}
      <label>
        {t('heroes.addField')}
        <input
          type="text"
          placeholder={t('heroes.fieldPlaceholder')}
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
      </label>
      <div className="edit-actions">
        <button
          type="button"
          className="save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" className="cancel-btn" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
